import { HierarcicalObjectReference, ObjectId } from "@novorender/data-js-api";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

import { Tree } from "components/objectTree/types";
import { getParentPaths } from "components/objectTree/utils";
import type { ObjectDataAccess } from "dataAccess";
import { ModelTreeNodeData } from "features/modelTree/types";
import { fetchDescendantsBatch } from "utils/preloadDescendants";
import { SelectionSet } from "utils/selectionSet";

/**
 * Ancestor-path building is O(loaded nodes × depth). Skip it only when the
 * highlight is large and already covers every loaded node, which is what
 * Select All produces. A large hidden or revealed set must not use this:
 * those folders are not all ancestors of the highlight.
 */
export const LARGE_SELECTION_THRESHOLD = 500;

function highlightCoversLoadedTree(tree: Tree<ModelTreeNodeData>, highlightedIds: SelectionSet | undefined): boolean {
    if (!highlightedIds || highlightedIds.size <= LARGE_SELECTION_THRESHOLD) {
        return false;
    }
    let loadedCount = 0;
    for (const node of Object.values(tree)) {
        if (node.path === "") {
            continue;
        }
        loadedCount++;
        if (!highlightedIds.has(node.data.object.id)) {
            return false;
        }
    }
    return loadedCount > 0;
}

// MAX_FETCH_PER_RUN has been removed. Previously, fetching was capped at 50
// nodes per effect run, which caused a cascade: after each batch completed
// `setTree(t => ({...t}))` changed the tree reference, re-triggering the
// effect for the next batch and causing ceil(n/50) consecutive re-renders
// (visible as sluggish scroll and blank rows after Cmd+A on a large scene).
// All needed nodes are now fetched in a single pMap batch (bounded by
// MAX_PRELOAD_CONCURRENCY = 5 concurrent requests), so only ONE tree spread
// and ONE re-render occur regardless of how many ancestors need preloading.

/**
 * Proactively fetches `descendants` for non-leaf tree nodes that don't have
 * them yet.  This allows {@link getGroupStateForIdSet} to propagate selection
 * state (e.g. from lasso / area-select) up to folder nodes even before the
 * user expands them in the UI.
 *
 * To avoid overwhelming the backend with requests, the fetch list is **scoped
 * to ancestor paths** of the current selection: only folder nodes that are
 * direct ancestors of a selected object already present in the tree are
 * considered.  Requests are batched with limited concurrency (see
 * {@link MAX_PRELOAD_CONCURRENCY}) and nodes whose own id overlaps the current
 * selection are fetched first so the UI can show accurate state as early as
 * possible.
 *
 * Once all outstanding nodes are populated the hook "touches" the tree
 * (`setTree(t => ({...t}))`) so that memos depending on the tree reference
 * recompute with the newly-available descendant data.
 */
export function usePreloadDescendants(
    tree: Tree<ModelTreeNodeData>,
    dataAccess: ObjectDataAccess | undefined,
    idSets: ReadonlyArray<SelectionSet>,
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>,
    highlightedIds?: SelectionSet,
): void {
    const setTreeRef = useRef(setTree);
    setTreeRef.current = setTree;

    useEffect(() => {
        const isSelected = (id: ObjectId) => idSets.some((ids) => ids.has(id));

        if (!dataAccess || idSets.length === 0) {
            return;
        }

        // Select All puts every loaded node into the highlight. Building ancestor
        // paths first would visit every path, so collect the folders directly.
        // Hidden and revealed ids stay on the ancestor walk below.
        const nodesToFetch: HierarcicalObjectReference[] = [];

        if (highlightCoversLoadedTree(tree, highlightedIds)) {
            // The highlight already covers every loaded node, so every folder is relevant.
            // Collect non-leaf nodes that are in the tree but haven't had their
            // descendants fetched yet. Exclude the root path ("").
            for (const node of Object.values(tree)) {
                if (node.path !== "" && !node.isLeaf && !node.loading && !node.data.object.descendants) {
                    nodesToFetch.push(node.data.object);
                }
            }
        } else {
            // Build the set of ancestor paths for selected objects that are
            // already present in the tree.  We only need descendants for folder
            // nodes on these ancestor paths – all other nodes are unrelated to
            // the current selection and can be skipped.
            const ancestorPaths = new Set<string>();
            for (const node of Object.values(tree)) {
                if (isSelected(node.data.object.id)) {
                    for (const p of getParentPaths(node.path)) {
                        ancestorPaths.add(p);
                    }
                }
            }

            // Collect non-leaf nodes that are in the tree but haven't had their
            // descendants fetched yet, if they either lie on an ancestor path of a
            // selected object or are themselves selected.  A selected folder needs
            // its own descendants to render "all": when a parent folder is selected
            // every sibling folder's own id is added to the selection too, but the
            // siblings are collapsed so they sit on no in-tree node's ancestor path
            // — without this they stay stuck on the indeterminate "some" state until
            // a manual collapse + re-expand.  This includes partially-loaded nodes
            // (children present but not all pages loaded) so that selection state
            // can propagate through unloaded portions.  This avoids fetching
            // descendants for the (potentially very large) set of unrelated folder
            // nodes. Exclude the root path ("") so we never preload descendants for
            // the entire scene.
            for (const node of Object.values(tree)) {
                if (
                    node.path !== "" &&
                    !node.isLeaf &&
                    !node.loading &&
                    !node.data.object.descendants &&
                    (ancestorPaths.has(node.path) || isSelected(node.data.object.id))
                ) {
                    nodesToFetch.push(node.data.object);
                }
            }
        }

        if (nodesToFetch.length === 0) {
            return;
        }

        // Prioritise nodes whose own id is in the selection – these are the
        // ones most likely to need descendant data for the current UI state.
        nodesToFetch.sort((a, b) => {
            const aSelected = isSelected(a.id) ? 0 : 1;
            const bSelected = isSelected(b.id) ? 0 : 1;
            return aSelected - bSelected;
        });

        const abortController = new AbortController();

        fetchDescendantsBatch(dataAccess, nodesToFetch, abortController.signal).then((populated) => {
            if (populated && !abortController.signal.aborted) {
                // Descendants are already on these objects. A new tree reference
                // invalidates the WeakMap caches in treeHelpers so the counters
                // rebuild. The effect run this causes finds nothing left to fetch
                // unless a newer tree (expand, children loaded) still needs some.
                setTreeRef.current((t) => ({ ...t }));
            }
        });

        return () => abortController.abort();
        // Re-run when the tree identity changes (nodes added/removed) or when
        // the selection changes.  The reducer hands a fresh `ids` record on every
        // selection mutation, so `idSets` reliably triggers re-runs; nodes whose
        // descendants were already populated are skipped by the filter above, so
        // redundant network requests are avoided.
    }, [tree, dataAccess, idSets, highlightedIds]);
}
